import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiThermometer, FiRadio, FiSettings, FiUsers } from 'react-icons/fi';

const iconMap = {
  'thermometer': FiThermometer,
  'radio': FiRadio,
  'settings': FiSettings,
  'users': FiUsers
};

function MilGradeLayer({ data }) {
  const { t } = useTranslation();

  // Icon mapping for pillars
  const pillarIcons = ['thermometer', 'radio', 'settings', 'users'];

  // Default pillars in English
  const defaultPillars = [
    { title: 'Environmental Readiness', description: 'MIL-STD-810 alignment for shock, vibration, temperature, humidity—applied as program specifications demand.' },
    { title: 'EMI/EMC Integration', description: 'MIL-STD-461 alignment for electromagnetic compatibility—integrated into the build process when required.' },
    { title: 'RAM Engineering', description: 'Reliability, Availability & Maintainability discipline—designed into the system from day one.' },
    { title: 'Human Engineering', description: 'Ergonomics, accessibility, and operator safety—engineered for real-world field conditions.' }
  ];

  // Get translated pillars array
  const translatedPillars = t('milGradeLayer.pillars', { returnObjects: true });
  
  // Use translated pillars if available, map with icons
  const pillars = (Array.isArray(translatedPillars) ? translatedPillars : defaultPillars).map((pillar, i) => ({
    ...pillar,
    icon: pillarIcons[i] || 'settings'
  }));
  const title = t('milGradeLayer.title', { defaultValue: 'MIL-Grade Engineering Layer' });
  const subtitle = t('milGradeLayer.subtitle', { defaultValue: '(Program Dependent)' });
  const microLine = t('milGradeLayer.microLine', { defaultValue: 'Program-dependent military engineering alignment is embedded during requirements translation — not added at the end.' });
  const footnote = t('milGradeLayer.footnote', { defaultValue: 'Exact standards alignment and verification scope depend on customer program requirements and contractual documentation.' });

  return (
    <div 
      id="mil-grade" 
      className="py-12 md:py-16" 
      style={{ backgroundColor: '#101957' }}
      data-testid="mil-grade-layer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">
            {title} <span className="font-normal opacity-70">{subtitle}</span>
          </h2>
          <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto">
            {microLine}
          </p>
        </motion.div>

        {/* 4 Pillars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 max-w-6xl mx-auto">
          {pillars.map((pillar, index) => {
            const Icon = iconMap[pillar.icon] || FiSettings;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, ease: "easeOut", delay: index * 0.08 }}
                className="p-5 rounded-lg"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
                data-testid={`mil-pillar-${index}`}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }}>
                  <Icon className="text-lg text-white/90" />
                </div>
                <h3 className="text-sm font-bold text-white mb-2 leading-tight">
                  {pillar.title}
                </h3>
                <p className="text-xs text-white/70 leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="text-center text-xs text-white/50"
        >
          {footnote}
        </motion.p>
      </div>
    </div>
  );
}

export default MilGradeLayer;
